import type {
  ToolCompleteConfig,
  ToolTrackingStepConfig,
} from '@analytics/models';
import {
  analyticsHash,
  E2E_TRACKING_ID_KEY,
  parseToolConfig,
} from '@analytics/utils';
import { FormGroup } from '@angular/forms';
import type {
  HomeBatteryOrderFormRendering,
  HomeBatteryOrderForm,
} from '@feh/homebattery-models';
import {
  RfaFormControl,
  RfaValidators,
  RfaComponents,
  type RfaAddress,
} from '@innogy/feh/rfa';
import {
  getLeadFormControls,
  getLeadConsentFormControls,
} from '@innogy/feh/shared';
import type { PostHomebatteryV1Data } from '@integration/api-client/feh';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-angular';

export function createForm(
  rendering: HomeBatteryOrderFormRendering,
  analyticsComponentName: string = rendering.componentName
): HomeBatteryOrderForm {
  return new FormGroup({
    ...getLeadFormControls(rendering, analyticsComponentName),
    ...getLeadConsentFormControls(rendering, analyticsComponentName),
    remarks: new RfaFormControl(
      '',
      {
        label: getFieldValue(rendering, 'RemarksLabel', 'Opmerkingen'),
        maxLength: 350,
        validators: [RfaValidators.maxLength(350)],
        dataAutomationId: 'homebattery-advice--remarks',
        errorMsgs: {
          maxlength: {
            msg: 'Je mag maximaal 350 tekens gebruiken',
            analytics: {
              componentName: analyticsComponentName,
            },
          },
        },
        analytics: {
          focusChangeTracker: {
            componentName: analyticsComponentName,
          },
        },
      },
      RfaComponents.TextArea
    ),
  });
}

export function mapFormToSubmitPayload(
  form: HomeBatteryOrderForm,
  rendering: HomeBatteryOrderFormRendering,
  brand: 'energiedirect' | 'essent',
  url: string,
  address: RfaAddress
): PostHomebatteryV1Data {
  return {
    requestBody: {
      type: 'homeBattery',
      context: {
        channel: 'Online',
        subChannel: rendering.fields?.LeadSubChannel?.value || undefined,
        brand,
        campaignId: rendering.fields?.LeadCampaignId?.value || undefined,
        productType: 'Thuisbatterij',
        requestType: 'Advies',
        website: url,
      },
      lead: {
        title: form.controls.title.value,
        initials: form.controls.name.controls.initials.value,
        prefix: form.controls.name.controls.insertion.value,
        lastName: form.controls.name.controls.lastName.value,
        addresses: {
          communication: {
            postcode: address.postcode,
            street: address.street,
            houseNumber: address.houseNumber.toString(),
            houseNumberExtension:
              address.houseNumberAddition?.trim() ?? undefined,
            city: address.city,
          },
        },
        phone: form.controls.phone.value,
        email: form.controls.email.value,
      },
      consent: {
        dataUsage: form.value.allowDataUsage,
        promotion: form.value.allowPromotionalUpdates,
      },
      metadata: {
        temperature: 4,
        createdOn: new Date().toISOString(),
        remarks: form.value.remarks || undefined,
      },
      products: [{ category: 'Thuisbatterij' }],
    },
  };
}

export function createTrackToolConfig(
  componentName: string,
  e2eTrackingId: string,
  form: HomeBatteryOrderForm
) {
  return parseToolConfig({
    toolName: componentName,
    steps: [
      {
        step: 0,
        stepName: '',
        id: 'form',
      },
    ] as unknown as ToolTrackingStepConfig<'form'>[],

    toolComplete: {
      selector: () => {},
      onComplete: () => ({
        step: 1,
        payload: {
          'e mailadres': analyticsHash(form.value.email ?? ''),
          'de servicepartner van essent mag naar aanleiding van deze aanvraag contact met mij opnemen':
            form.value.allowDataUsage,
          'ik wil graag per e mail op de hoogte blijven van interessante aanbiedingen en acties van essent en haar partners':
            form.value.allowPromotionalUpdates,
          [E2E_TRACKING_ID_KEY]: e2eTrackingId,
        },
      }),
    } as unknown as ToolCompleteConfig,
  });
}
